import './utils/suppressViteClientLogs';
import React from 'react';
import ReactDOM from 'react-dom/client';
import AppProviders from './AppProviders';
import reportWebVitals, { logIfFCPAbove500 } from './reportWebVitals';
import App from './App';
import './style/global.less';
import { setupDeferredGTM } from './services/gtm';
import maintainerMetrics from './services/maintainerMetrics';
import { STORAGE_KEYS, readStorageItem, writeStorageItem } from './utils/storage';
import { dataFlowLog } from './utils/dataFlowLogger';
import { requestWindowReload } from './utils/reloadCoordinator';

const activateDeferredStyles = () => {
  if (typeof document === 'undefined') return;
  const fontLink = document.querySelector('link[data-ls-fonts-preload]');
  if (fontLink && fontLink.media !== 'all') {
    fontLink.media = 'all';
  }
};

// NOTE: the old "auth-token-error" recovery that wiped all auth storage and
// forced a session-expiry lived here. It escalated any single unauthenticated
// 401 (including harmless post-login races) into a full logout. 401 handling is
// now owned entirely by the API client's refresh-and-retry path
// (services/apiClient.js + services/authBridge.js), so this recovery is gone.

if (typeof window !== 'undefined') {
  maintainerMetrics.initializeMaintainerMetrics();

  reportWebVitals((metric) => {
    maintainerMetrics.recordWebVitalMetric(metric);
    logIfFCPAbove500(metric);
  }, {
    onReport: () => {
      if (document.visibilityState === 'hidden') {
        void maintainerMetrics.flushPendingSamples({ useBeacon: true });
      }
    },
  });

  activateDeferredStyles();
}

if (typeof window !== 'undefined') {
  // Defer GTM until first user interaction to keep baseline critical path lean.
  setupDeferredGTM({
    containerId: import.meta.env.VITE_GTM_ID,
    enabled: import.meta.env.PROD,
  });

  // Guard against transient dynamic import chunk errors (often seen on mobile/PWA updates)
  const lastReloadKey = STORAGE_KEYS.CHUNK_ERROR_LAST_RELOAD;
  window.addEventListener('unhandledrejection', (event) => {
    try {
      const reason = event && event.reason ? String(event.reason.message || event.reason) : '';
      const isChunkErr = reason.includes('Loading chunk') ||
                         reason.includes('ChunkLoadError') ||
                         reason.includes('dynamically imported module');
      if (!isChunkErr) return;
      const now = Date.now();
      const last = Number(readStorageItem(lastReloadKey, { area: 'session', fallback: 0 }) || 0);
      dataFlowLog('main.chunk-error.detected', {
        reason,
        ageMs: last ? now - last : null,
      });
      if (!last || now - last > 15000) {
        writeStorageItem(lastReloadKey, String(now), { area: 'session' });
        requestWindowReload('main.chunk-error', {
          source: 'unhandled-rejection',
          detail: {
            reasonSnippet: reason.slice(0, 120),
          },
        });
      }
    } catch {
      // ignore
    }
  });
}

// Create the root and render the app
const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <AppProviders>
    <App />
  </AppProviders>
);
